// src/utils/stagingIntake.js
//
// Turns a listing file into the set of answers Register Redesign's intake form
// expects, so an agent doesn't retype the same twelve things for every listing.
//
// The form is a 17hats questionnaire embedded on registerredesign.com/contact.
// Field names (q27029948…) and the dropdown option text below were read off the
// live form. The option strings use EN DASHES (–), not hyphens; 17hats matches
// its dropdowns on exact text, so those characters are load-bearing.
//
// Sharon can edit her form at any time. Nothing here silently papers over that:
// each field carries her question label, and the filler verifies the label
// still matches before it types anything (see buildFillerBookmarklet).

// The form lives inside an iframe on her contact page. A filler running on the
// parent page can't reach across that origin boundary, so we open the 17hats
// form directly instead.
export const STAGING_FORM_URL =
  "https://registerredesign.17hats.com/p#/embed/zfgdbzkczwrdbwwhdbfzbwknpvvdbcpn";
export const STAGER_NAME = "Register Redesign";
export const STAGER_EMAIL = "sharon@registerredesign.com";

export const SIZE_OPTIONS = [
  "Under 1,500 sq ft",
  "1,500–2,500 sq ft",
  "2,500–4,000 sq ft",
  "4,000+ sq ft",
];
export const INQUIRY_OPTIONS = [
  "Vacant Staging Bid",
  "Staging Advice (Starting at $300)",
  "Occupied Staging Bid",
];
export const STATUS_OPTIONS = [
  "Preparing to List",
  "Currently Listed",
  "New Construction",
  "Occupied by Seller",
  "Vacant",
];
export const ROOM_OPTIONS = [
  "Living Room",
  "Dining Room",
  "Kitchen",
  "Primary Bedroom",
  "Secondary Bedrooms",
  "Office",
  "Entire Home",
];
export const PAYER_OPTIONS = ["Client", "Agent", "Client and Agent"];
export const BUDGET_OPTIONS = ["$1,500–$3,000", "$3,000–$5,000", "$5,000+"];

// "How did you hear about us?" — Henry's call: we're a standing referral
// partner, so this never needs answering. Listed here so the filler knows to
// leave it alone rather than treating it as a field it failed to find.
export const REFERRAL_FIELD = "q27029963";

// Her form in order. `source` is documentation for the request dialog: where
// each answer comes from, so an agent can see what's being sent on their behalf.
export const STAGING_FIELDS = [
  { name: "q27029948", label: "Please provide your full name", type: "text", source: "Assigned agent" },
  { name: "q27029949", label: "I am the…", type: "select", source: "Always this" },
  { name: "q27029950", label: "Email Address", type: "text", source: "Assigned agent" },
  { name: "q27029951", label: "Contact Phone Number", type: "text", source: "Assigned agent" },
  { name: "q27029952", label: "Staging Property Address", type: "text", source: "Listing file" },
  { name: "q27029953", label: "Property size", type: "select", source: "You" },
  { name: "q27029954", label: "What are you inquiring about?", type: "select", source: "Occupancy" },
  { name: "q27029955", label: "Current status of the property", type: "select", source: "Occupancy" },
  { name: "q27029956", label: "Rooms to stage", type: "checkbox", source: "You" },
  { name: "q27029957", label: "Client Name", type: "text", source: "First client on file" },
  { name: "q27029958", label: "Client email", type: "text", source: "First client on file" },
  { name: "q27029959", label: "Who is paying", type: "select", source: "You" },
  { name: "q27029960", label: "Estimated budget", type: "select", source: "You" },
  { name: "q27029961", label: "What is your ideal date?", type: "text", source: "Target list date" },
  { name: "q27029962", label: "Any additional info", type: "textarea", source: "You", optional: true },
];

// Everyone on the team requests staging except accountants, who never touch a
// listing before it goes live.
export function canRequestStaging(user) {
  if (!user) return false;
  return user.role !== "Accountant";
}

const occupancy = (deal) => String(deal?.occupied || "").trim().toLowerCase();

export function deriveStatus(deal) {
  const o = occupancy(deal);
  if (o === "yes") return "Occupied by Seller";
  if (o === "no") return "Vacant";
  return "";
}

export function deriveInquiry(deal) {
  // A consult-only file wants advice, not a full bid, regardless of occupancy.
  if (String(deal?.staging || "").trim().toLowerCase() === "consult") {
    return "Staging Advice (Starting at $300)";
  }
  const o = occupancy(deal);
  if (o === "yes") return "Occupied Staging Bid";
  if (o === "no") return "Vacant Staging Bid";
  return "";
}

// Date inputs store YYYY-MM-DD; her form is a plain text box that reads as
// MM/DD/YYYY like everything else Henry sees.
export function toUsDate(value) {
  const v = String(value || "").trim();
  if (!v) return "";
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  return v;
}

const agentName = (agent) =>
  `${agent?.firstname || ""} ${agent?.lastname || ""}`.trim();

const isBlank = (v) => (Array.isArray(v) ? v.length === 0 : !String(v || "").trim());

/**
 * Build the answer set for one listing.
 *
 * @param {object}   deal    the listing deal document
 * @param {object}   agent   the assigned agent's `users` record
 * @param {object[]} clients normalized contacts for deal.customers
 * @param {object}   answers the four things the file can't know, from the dialog
 * @param {object}   overrides values typed in the dialog for questions the file
 *                   couldn't answer, keyed by her field name. Real listings are
 *                   routinely missing occupancy, list date or a client email,
 *                   and those used to reach Sharon blank.
 * @returns {{fields: object[], missing: string[]}} fields carries only answers
 *          that actually resolved; missing names the ones that came out blank,
 *          which is exactly what the dialog turns into questions.
 */
export function buildStagingPayload({
  deal,
  agent,
  clients,
  answers,
  overrides,
} = {}) {
  const a = answers || {};
  const o = overrides || {};
  const client = (clients || [])[0] || {};

  const resolved = {
    q27029948: agentName(agent),
    q27029949: "Realtor / Listing Agent",
    q27029950: agent?.email || "",
    q27029951: agent?.phonenumber || "",
    q27029952: deal?.propertyaddress || "",
    q27029953: a.size || "",
    q27029954: deriveInquiry(deal),
    q27029955: deriveStatus(deal),
    q27029956: a.rooms || [],
    q27029957: client.name || "",
    q27029958: client.email || "",
    q27029959: a.payer || "",
    q27029960: a.budget || "",
    q27029961: toUsDate(deal?.targetListDate),
    q27029962: (a.notes || "").trim(),
  };

  const fields = [];
  const missing = [];

  STAGING_FIELDS.forEach((spec) => {
    // A typed override wins: the agent is looking at the file and Sharon's
    // question side by side, so they know better than the derivation does.
    const override = o[spec.name];
    const value = isBlank(override) ? resolved[spec.name] : override;
    if (isBlank(value)) {
      // `missing` means "the file couldn't tell us this" — a real gap worth
      // warning about. Optional questions, and the four the agent answers in
      // the dialog, are not gaps: the dialog gates on those separately.
      if (!spec.optional && spec.source !== "You") missing.push(spec.label);
      return;
    }
    fields.push({
      name: spec.name,
      label: spec.label,
      type: spec.type,
      source: spec.source,
      value,
    });
  });

  return { fields, missing };
}
